/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from '@chakra-ui/react';
import AutoComplete from "react-select";

interface PayloadType {
    [key: string]: any;
}

const initialData: {
    suba_id: string;
} = {
    suba_id: "",
};

const AgentAssignSubaccountModal: React.FC<{
    setSwalProps: any;
    agentDetail: any;
    assignedSubaccountIds: any;
    isOpen: boolean;
    onClose(): void;
    triggerReloadList(): void;
}> = ({ assignedSubaccountIds, setSwalProps, isOpen, onClose, agentDetail, triggerReloadList }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [subaccountList, setSubaccountList] = useState<any>(null);
    const [newAssign, setNewAssign] = useState<any>(initialData);
    const [errorMessage, setErrorMessage] = useState<any>("");

    const subaccountAutoCompleteRef = useRef(null);
    const [subaccountAutoCompleteOptions, setSubaccountAutoCompleteOptions] = useState<any>(null);

    const closeModal = async () => {
        onClose();
        setSubaccountList(null);
        setSubaccountAutoCompleteOptions(null);
        setErrorMessage("");
        setNewAssign({ ...newAssign, suba_id: "" });
    };

    const submitHandler = async () => {

        setErrorMessage("");

        const subaccount = subaccountList.find((x: any) => x.id === newAssign.suba_id);

        newAssign.agnt_id = agentDetail.id;
        newAssign.action = "add";
        newAssign.username = localStorage.getItem("username");
        newAssign.userRole = localStorage.getItem("userRole")?.toLowerCase();
        newAssign.agentName = agentDetail.suba_name;
        newAssign.subaccountName = subaccount.suba_name;

        try {
            setIsProcessing(true);
            await axios.post(
                process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole")?.toLowerCase()}/agent/action/agent-subaccount-manage`,
                newAssign
            )
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Assign subaccount successfully",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                                triggerReloadList();
                            },
                        });
                    } else {
                        setIsProcessing(false);
                        setErrorMessage('Assign subaccount has been failed');
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    if (error.response) {
                        setErrorMessage(error.response.data.message);
                    } else {
                        setErrorMessage('Assign subaccount has been failed');
                    }
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const fetchSubaccounts = async () => {
        try {

            const queryObject: PayloadType = {
                entity_type: "SUBA#",
                is_agent_unassigned: true
            };

            const queryString = Object.keys(queryObject)
                .map(
                    (key) =>
                        `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
                )
                .join("&");

            const response = await axios.get(
                process.env.REACT_APP_API_URL +
                `/api/subaccount/get-all-subaccounts?${queryString}`
            );

            const data = response.data?.subaccounts?.payload;
            const filteredData = data.filter((item: any) => !assignedSubaccountIds.includes(item.id));

            setSubaccountList(filteredData);
        } catch (error) {
            setIsLoading(false);
            console.error("Error fetching data", error);
        }
    };

    const handleSubaccountAutoCompleteChange = (item: any) => {
        if (item !== null) {
            setNewAssign({ ...newAssign, suba_id: item.value });
        } else {
            setNewAssign({ ...newAssign, suba_id: "" });
        }
    };

    const noSubaccountOptionsMessageRenderer = ({
        inputValue,
    }: {
        inputValue: string;
    }) => {
        return <div className="text-sm">Subaccount not found for "{inputValue}"</div>;
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchSubaccounts();
        };

        if (isOpen && subaccountAutoCompleteOptions === null) {
            fetchData();
        }
    }, [isOpen]); // eslint-disable-line

    useEffect(() => {
        if (subaccountList !== null) {
            setIsLoading(true);
            let rebuildListArray: any = [];
            if (subaccountList.length > 0) {
                subaccountList.forEach((i: any) => {
                    let newObj = {
                        value: i.id,
                        label: i.suba_name,
                    };
                    rebuildListArray.push(newObj);
                });
            }

            setSubaccountAutoCompleteOptions(rebuildListArray);
            setIsLoading(false);
        }
    }, [subaccountList]); // eslint-disable-line

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                    <ModalBody className="p-6 md:p-7">
                        <div className="mb-4 flex items-start gap-3">
                            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M12 4v16m8-8H4" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Assign New Account
                                </h1>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    Assign a subaccount to {agentDetail?.suba_name}
                                </p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                                Subaccount *
                            </label>
                            <AutoComplete
                                ref={subaccountAutoCompleteRef}
                                isDisabled={
                                    isLoading ||
                                    isProcessing ||
                                    subaccountAutoCompleteOptions === null ||
                                    subaccountAutoCompleteOptions.length === 0
                                }
                                placeholder={
                                    subaccountAutoCompleteOptions === null
                                        ? "Retrieving..."
                                        : "Please Select"
                                }
                                options={subaccountAutoCompleteOptions}
                                classNamePrefix="custom-autocomplete"
                                className="autocomplete-override"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                                onChange={(item: any) =>
                                    handleSubaccountAutoCompleteChange(item)
                                }
                                noOptionsMessage={noSubaccountOptionsMessageRenderer}
                                isClearable
                            />

                            {errorMessage && (
                                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                                    {errorMessage}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                onClick={() => closeModal()}
                                isDisabled={isProcessing || !subaccountList}
                                className="btn btn-default"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => submitHandler()}
                                disabled={newAssign.suba_id === "" || isProcessing || !subaccountList}
                                isLoading={isProcessing}
                                loadingText='Processing...'
                                className="btn btn-primary">
                                Assign Account
                            </Button>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default AgentAssignSubaccountModal;